import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';

import { AdcampaignPlaybackReportTypeForInput } from '@shared/api/graphql/generated';

import {
  getReportPeriodCalendarMaxDate,
  getReportPeriodDateBounds,
  getReportPeriodInvalidReason,
} from '../ReportRequestForm/reportPeriod';
import { EMAIL_REGEXP } from './consts';
import { TEXTS } from './texts';
import type { DailyReportInputValues, DailyReportMutationInput } from './types';

const isPositiveFiniteNumber = (value: number) => Number.isFinite(value) && value > 0;

export const getDailyReportDateBounds = getReportPeriodDateBounds;

export const getDailyReportCalendarMaxDate = (startDate: Date | null, endDate: Date | null, now = new Date()) => {
  const { maxDate } = getDailyReportDateBounds(now);

  if (!startDate || endDate) {
    return maxDate;
  }

  return getReportPeriodCalendarMaxDate(startDate, endDate, maxDate);
};

export const getDailyReportDateRangeError = (startDate: Date, endDate: Date, now = new Date()): string | null => {
  const invalidReason = getReportPeriodInvalidReason(startDate, endDate, getDailyReportDateBounds(now));

  return invalidReason ? TEXTS.DATE_RANGE_ERROR : null;
};

export const getPreparedReportEmails = (emails: string[]) => {
  const usedEmails = new Set<string>();

  return emails
    .map((email) => email.trim())
    .filter((email) => {
      if (!email || !EMAIL_REGEXP.test(email)) {
        return false;
      }

      const normalizedEmail = email.toLowerCase();

      if (usedEmails.has(normalizedEmail)) {
        return false;
      }

      usedEmails.add(normalizedEmail);

      return true;
    });
};

export const getNumericPointIds = (addressIds: string[]): number[] =>
  Array.from(new Set(addressIds.map(Number).filter(isPositiveFiniteNumber)));

export const buildDailyReportInput = ({
  companyId,
  endDate,
  externalRecipientEmails,
  mediafileId,
  selectedAddressIds,
  selectedRecipientEmails,
  startDate,
}: DailyReportInputValues): DailyReportMutationInput | null => {
  if (!startDate || !endDate || !mediafileId) {
    return null;
  }

  const pointIds = getNumericPointIds(selectedAddressIds);
  const emailsTo = getPreparedReportEmails([...selectedRecipientEmails, ...externalRecipientEmails]);

  if (pointIds.length === 0 || emailsTo.length === 0) {
    return null;
  }

  return {
    adcampaignReportType: AdcampaignPlaybackReportTypeForInput.AudioAds,
    companyId,
    dateRange: {
      dateFrom: DateUtils.getStartOfDate(startDate).toISOString(),
      dateTo: DateUtils.getEndOfDate(endDate).toISOString(),
    },
    emailsTo,
    mediafileId,
    pointIds,
  };
};
